// Shared token-skipping helpers used by both clauseScanner (SELECT) and
// classifyPosition.dml (INSERT/UPDATE/DELETE/MERGE/WITH). Each helper advances
// the cursor past one non-code region (string, comment, or quoted identifier)
// and returns the new index — or the original index if the cursor isn't at
// that region. Pure function, no tree-sitter.
// PRD: Ralph/SemanticResolverReadOnly/PRD.md § Slice: Position Classifier — DML.

import type { ParseOptions } from '../../sql-grammar/model';

export interface SkipContext {
  readonly supportsBacktick: boolean;
  readonly supportsBracket: boolean;
  readonly supportsDollar: boolean;
  readonly supportsHashComment: boolean;
}

export function skipContextFor(options: ParseOptions): SkipContext {
  const engine = options.engine;
  return {
    supportsBacktick: engine === 'mysql' || engine === 'sqlite',
    supportsBracket: engine === 'mssql' || engine === 'sqlite',
    supportsDollar: engine === 'postgres',
    supportsHashComment: engine === 'mysql',
  };
}

// Try to skip a non-code region at position i. Returns the index just past the
// region, or -1 if no skip applied at this position.
// eslint-disable-next-line complexity
export function trySkipNonCode(
  source: string,
  i: number,
  endExclusive: number,
  ctx: SkipContext,
): number {
  const c = source[i];
  const c2 = source[i + 1] ?? '';

  if (c === '-' && c2 === '-') {
    let j = i + 2;
    while (j < endExclusive && source[j] !== '\n') j++;
    return j;
  }
  if (ctx.supportsHashComment && c === '#') {
    let j = i + 1;
    while (j < endExclusive && source[j] !== '\n') j++;
    return j;
  }
  if (c === '/' && c2 === '*') {
    let j = i + 2;
    while (j < endExclusive && !(source[j] === '*' && source[j + 1] === '/')) j++;
    if (j < endExclusive) j += 2;
    return j;
  }
  if (c === "'") {
    let j = i + 1;
    while (j < endExclusive) {
      if (source[j] === "'") {
        if (source[j + 1] === "'") { j += 2; continue; }
        j++; return j;
      }
      j++;
    }
    return j;
  }
  if (c === '"') {
    let j = i + 1;
    while (j < endExclusive) {
      if (source[j] === '"') {
        if (source[j + 1] === '"') { j += 2; continue; }
        j++; return j;
      }
      j++;
    }
    return j;
  }
  if (ctx.supportsBacktick && c === '`') {
    let j = i + 1;
    while (j < endExclusive && source[j] !== '`') j++;
    if (j < endExclusive) j++;
    return j;
  }
  if (ctx.supportsBracket && c === '[') {
    let j = i + 1;
    while (j < endExclusive && source[j] !== ']') j++;
    if (j < endExclusive) j++;
    return j;
  }
  if (ctx.supportsDollar && c === '$') {
    const match = source.slice(i).match(/^\$([A-Za-z_][A-Za-z0-9_]*)?\$/);
    if (match) {
      const tag = match[0];
      let j = i + tag.length;
      while (j < endExclusive && !source.startsWith(tag, j)) j++;
      if (j < endExclusive) j += tag.length;
      return j;
    }
  }
  return -1;
}

// Skip ONLY comment trivia at position i (line / hash / block comments).
// Unlike `trySkipNonCode`, this NEVER skips strings or quoted identifiers —
// at a clause-content boundary those are real content (`"c"."country"`,
// `'lit' AS x`), not junk. Use this for boundary trims; keep `trySkipNonCode`
// for keyword/structure scanners, which MUST skip quoted regions atomically.
// Returns the index just past the comment, or -1 if not at a comment.
export function trySkipLeadingTrivia(
  source: string,
  i: number,
  endExclusive: number,
  ctx: SkipContext,
): number {
  const c = source[i];
  const c2 = source[i + 1] ?? '';
  if (c === '-' && c2 === '-') {
    let j = i + 2;
    while (j < endExclusive && source[j] !== '\n') j++;
    return j;
  }
  if (ctx.supportsHashComment && c === '#') {
    let j = i + 1;
    while (j < endExclusive && source[j] !== '\n') j++;
    return j;
  }
  if (c === '/' && c2 === '*') {
    let j = i + 2;
    while (j < endExclusive && !(source[j] === '*' && source[j + 1] === '/')) j++;
    if (j < endExclusive) j += 2;
    return j;
  }
  return -1;
}
